/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react-native/no-color-literals */
import * as React from 'react';
import {
  Image,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import theme from '../../common/theme';
import { useTranslation } from '@oolio-group/localization';
import { DisplayScreen } from '@oolio-group/domain';
import Carousel from 'react-native-reanimated-carousel';

export interface BrandingProps {
  storeName?: string;
  screenConfig?: DisplayScreen;
  brandColor?: string;
  width?: number;
  height?: number;
  children?: React.ReactNode;
}

export const Branding: React.FC<BrandingProps> = ({
  storeName = '',
  screenConfig,
  brandColor,
  children,
  width,
  height,
}) => {
  const { translate } = useTranslation();
  const windowDimensions = useWindowDimensions();

  // Use provided props OR fall back to reactive dimensions
  const actualWidth = width ?? windowDimensions.width;
  const actualHeight = height ?? windowDimensions.height;

  const assetCount = screenConfig?.assets?.length ?? 0;

  const renderLogoOrName = () => {
    if (children) return null;

    const logoUri = screenConfig?.logoUri;
    const hasAssets = !!screenConfig?.assets?.length;

    return logoUri ? (
      <Image
        source={{ uri: logoUri }}
        style={styles.logoImage}
        resizeMode="contain"
      />
    ) : !hasAssets ? (
      <View testID="navigate-login-button">
        <Text style={styles.logoText} testID="store-name">
          {storeName}
        </Text>
      </View>
    ) : null;
  };

  return (
    <View
      style={[styles.container, { width: actualWidth, height: actualHeight }]}
    >
      <View
        style={[
          styles.background,
          {
            width: actualWidth,
            height: actualHeight,
            backgroundColor: brandColor || theme.colors.deepPurple,
          },
        ]}
      />
      {screenConfig && assetCount > 1 ? (
        <Carousel
          width={actualWidth}
          height={actualHeight}
          autoPlay={true}
          autoPlayInterval={5000}
          loop={assetCount > 1}
          scrollAnimationDuration={1000}
          data={screenConfig?.assets ?? []}
          renderItem={({ item }) => (
            <Image
              source={{
                uri: item.uri,
              }}
              style={[
                styles.background,
                {
                  width: actualWidth,
                  height: actualHeight,
                },
              ]}
              resizeMode="cover"
            />
          )}
        />
      ) : assetCount === 1 ? (
        <Image
          source={{
            uri: screenConfig?.assets?.[0]?.uri,
          }}
          style={[
            styles.background,
            {
              width: actualWidth,
              height: actualHeight,
            },
          ]}
          resizeMode="cover"
        />
      ) : null}
      <View
        style={[
          styles.blackPanel,
          { width: actualWidth, height: actualHeight },
        ]}
      />
      {renderLogoOrName()}
      {!children && (
        <View style={styles.bottomLogo}>
          <Text style={styles.bottomText}>
            {translate('cdsCartScreen.poweredBy')}
          </Text>
          <Image source={{ uri: theme.tillWhiteLogo }} style={styles.logo} />
        </View>
      )}
      <View
        style={{ position: 'absolute', flex: 1, width: '100%', height: '100%' }}
      >
        {!!children && children}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  background: {
    position: 'absolute',
    flex: 1,
  },
  blackPanel: {
    position: 'absolute',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
  logoText: {
    fontSize: 38,
    lineHeight: 38,
    fontWeight: 'bold',
    color: theme.colors.white,
  },
  logoImage: {
    width: 100,
    height: 100,
    position: 'absolute',
    borderRadius: 12,
  },
  logo: {
    width: 200,
    height: 30,
    resizeMode: 'contain',
  },
  bottomLogo: {
    position: 'absolute',
    bottom: 30,
    opacity: 0.3,
    alignItems: 'center',
  },
  bottomText: {
    marginBottom: 10,
    alignSelf: 'center',
    color: theme.colors.white,
    fontSize: theme.fontSize.xs,
  },
});
